import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { first, map } from 'rxjs/operators';
import {
  AutomationsStatus,
  BilletWebEvent,
  CategoriesGroup,
  Category,
  Currencies,
  Event,
  EventCreateOptions,
  EventStatus,
  Language,
  Organizer,
  OrganizerAccessRight,
  PayoutPolicy,
  Platforms,
  TicketingPlatform,
  defaultCategoriesGroup,
  defaultCategory,
} from '../../../models';
import { PartnerUtils } from './utils';

const billetwebSessionDate = (sessionDate: string) => {
  // Regex to extract only date and time as sessionDate may contains other information such as venue name
  const regex = /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2})/;
  const match = sessionDate?.match(regex);
  if (match) {
    return new Date(match[1]);
  }
  return undefined;
};

@Injectable({
  providedIn: 'root',
})
export class BilletwebEventService {
  private http = inject(HttpClient);

  configUrl = environment.apiUrl;

  private getBilletWebEvents(options?: {removeExpired?: boolean}) {
    let route = `${this.configUrl}w/a/billetwebEvents`;
    if (options.removeExpired) {
      route = route + '?removeExpired=true';
    }
    return this.http.get<{
      billetwebEvents: BilletWebEvent[],
      reelaxEvents: Event[],
      reelaxOrganizers: Organizer[],
    }>(route).pipe(
      first(),
    );
  }

  public loadEvents(options: EventCreateOptions) {
    return this.getBilletWebEvents(options)
      .pipe(
        map( (events) => events?.billetwebEvents?.map((e) => this.mapBilletWebToReelaxEvent(
          e,
          events.reelaxEvents,
          events.reelaxOrganizers,
          options.splitSessions,
          options.eventTemplate,
          options.priceScale,
          options.defaultStatus,
        ))),
      );
  }

  private dateToGroupName(date: Date) {
    return date.toLocaleString('fr-FR', {
      // eslint-disable-next-line sort-keys
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      month: '2-digit',
      timeZone: 'Europe/Paris',
      year: '2-digit',
    });
  }

  public mapBilletWebToReelaxEvent(
    billetweb: BilletWebEvent,
    reelaxEvents: Event[],
    reelaxOrganizers: Organizer[],
    splitSessions: boolean,
    eventTemplate: Event,
    priceScale: number,
    defaultStatus: EventStatus,
  ) {
    if (splitSessions && Object.keys(billetweb.sessions)?.length > 0) {
      billetweb.categories = Object.keys(billetweb.sessions)
        .map( (sessionKey) => {
          const sessionDate = billetwebSessionDate(billetweb.sessions[sessionKey]);
          const sessionName = this.dateToGroupName(sessionDate);
          return billetweb.categories.map( (cat, i) => ({
            ...cat,
            id: `${cat.id}@${sessionKey}`,
            parents_name: [cat.parents_name, sessionName].filter( (n) => !!n).join(' - '),
            sessionDate,
          }));
        }).flat();
    }
    const categoriesGroupNames = new Set([...billetweb.categories.map( (cat) => cat.parents_name)]);
    const newEventDateStart = new Date(billetweb.date_start);
    return {
      ...eventTemplate,
      id: reelaxEvents.find(
        (r) => r.TicketingPlatforms.some((t) => t.platformEventId === `${billetweb.id}`),
      )?.id,
      name: billetweb.name,
      organizerName: eventTemplate?.organizerName ?? billetweb.organizer.name,
      location: billetweb.address.city + ', ' + billetweb.address.country,
      dateStart: newEventDateStart,
      dateEnd: billetweb.date_end ? new Date(billetweb.date_end) : undefined,
      resaleEnd: PartnerUtils.computeDateRelativeTo(
        eventTemplate?.resaleEnd,
        newEventDateStart,
        eventTemplate?.dateStart,
      ),
      imgUrl: billetweb.cover,
      color: billetweb.color ? '#' + billetweb.color : undefined,
      address: [
        billetweb.address.address1,
        billetweb.address.address2,
        billetweb.address.city,
        billetweb.address.zip_code,
        billetweb.address.state,
        billetweb.address.country,
      ].filter((x) => !!x).join(', '),
      isPartner: true,
      allowAPIImageUpdate: true,
      allowPartnerUpdate: true,
      allowPayinRefund: true,
      status: defaultStatus ? defaultStatus : EventStatus.Online,
      reelaxEvent:  reelaxEvents.find(
        (r) => r.TicketingPlatforms.some((t) => t.platformEventId === `${billetweb.id}`),
      ),
      ticketshop: billetweb.ticketshop,
      TicketingPlatforms: [new TicketingPlatform({
        platform: Platforms.BilletWeb,
        platformEventId: billetweb.id.toString(),
        payoutPolicy: PayoutPolicy.Auto,
      })],
      Categories: billetweb.categories.map( (b, i) => new Category({
        ...defaultCategory,
        ...(eventTemplate?.Categories?.[0]),
        available: b.price > 0,
        categoriesGroupId: (b.parents_name as any),
        currency: Currencies.Euro,
        expiryDate: b.sessionDate ? new Date(b.sessionDate) : undefined,
        description: b.description ? {[Language.frFR]: b.description?.replace(/\n/g, '<br>')} : undefined,
        minimalSalePrice: priceScale ? Math.round(b.price * priceScale * 100) : Math.round(b.price * 50),
        maximalSalePrice: Math.round(b.price * 100),
        name: b.short_name,
        price: Math.round(b.price * 100),
        platformsForeignIds: {[Platforms.BilletWeb]: `${b.id}`},
        order: i,
      })),
      CategoriesGroups: [...categoriesGroupNames].map( (group, i) => new CategoriesGroup({
        ...defaultCategoriesGroup,
        name: group,
        order: i,
        platformsForeignIds: {[Platforms.BilletWeb]: group},
      })),
      PostalAddress: {
        address1: billetweb.address.address1,
        address2: billetweb.address.address2,
        city: billetweb.address.city,
        zip_code: billetweb.address.zip_code,
        country: billetweb.address.country,
      },
      Organizers: eventTemplate?.Organizers?.length >= 1 ? [
        ...eventTemplate.Organizers,
        new Organizer({
          ...reelaxOrganizers.find(
            (o) => o.platformsForeignIds[Platforms.BilletWeb] === billetweb.organizer.id?.toString(),
          ),
          EventOrganizer: {
            accessRight: OrganizerAccessRight.Owner,
          },
        }),
      ] : [new Organizer({
        ...reelaxOrganizers.find(
          (o) => o.platformsForeignIds[Platforms.BilletWeb] === billetweb.organizer.id?.toString(),
        ),
        EventOrganizer: {
          accessRight: OrganizerAccessRight.Owner,
        },
      })],
      Automations: eventTemplate?.Automations?.map( (automation) => {
        const oldAutoTime = new Date(automation.time);
        const templateDateStart = automation?.eventDateStart ?
          new Date(automation?.eventDateStart) : new Date(eventTemplate?.dateStart);
        const newTime = new Date(
          newEventDateStart.getTime() - (templateDateStart.getTime() - oldAutoTime.getTime()),
        ).toString();
        return {
          status: AutomationsStatus.Created,
          task: automation.task,
          time: newTime,
          type: automation.type,
        };
      }),
      WaitListConfig: eventTemplate?.WaitListConfig ?? undefined,
      EventPersonalization: eventTemplate?.EventPersonalization ?? undefined,
    };
  }

}
